import { lazy, Suspense, useRef, useState } from "react";
import { toast } from "sonner";
import type { Lead, SemanticProfileMatch } from "../../../../client";
import {
  useDeleteLead,
  useGetOrganization,
  useUpdateLead,
} from "../../../../services";
import { cn } from "../../../../components/utils/cn";
import { Image, LoadingSpinner } from "../../../../components";
import { getImageUrl } from "../../../../utils/image";
import type { LeadRowActiveState } from "../../../../state/home";
import { getNextLeadRowActiveState, homeState } from "../../../../state/home";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStatsigClient } from "../../../../hooks";
import { useGetMatchResults } from "../../../../services/organtization/semantic-match";
import EditorSkillTagButton from "../../../../components/atoms/EditorSkillTagButton";
import { LeadButton } from "./LeadButton";
import { LeadEventsButton } from "./LeadEventsButton";
import { LeadTemperatureButton } from "./LeadTemperatureButton";
import { LeadMenuButton } from "./LeadMenuButton";
import { LeadOpenLinkButton } from "./LeadOpenLinkButton";
import { LeadEditLinkButton } from "./LeadEditLinkButton";

// Lazy import to break circular dep: ChildLeadRow ← LeadPanelContent ← ChildLeadRow
const LeadPanelContent = lazy(() =>
  import("./LeadPanelContent").then((m) => ({ default: m.LeadPanelContent })),
);

type ChildTab = LeadRowActiveState | "match";

interface Props {
  lead: Lead;
  organizationId: string;
  index: number;
}

export const ChildLeadRow = ({ lead, organizationId, index }: Props) => {
  if (!lead.id) throw new Error("Missing lead ID");

  const toastId = useRef<string | number>();
  // Use local state instead of global homeState so toggling this row
  // doesn't collapse the parent lead's assignment panel.
  const [activeTab, setActiveTab] = useState<ChildTab>(false);
  const toggleTab = (tab: ChildTab) =>
    setActiveTab((prev) => (prev === tab ? false : (tab as ChildTab)));

  const statsigClient = useStatsigClient();
  const showMatrix = statsigClient?.checkGate("competence_matrix") ?? false;

  const { data: orgData } = useGetOrganization(organizationId);

  // Fetch match results from the parent lead (already cached by SemanticMatchResultsList)
  const { data: matchData } = useGetMatchResults(
    organizationId,
    lead.parentLeadId ?? "",
    { enabled: !!lead.parentLeadId },
  );

  const deleteLead = useDeleteLead(organizationId, {
    onError: () => {
      toast.error("Something went wrong", { id: toastId.current });
    },
  });

  const updateLead = useUpdateLead(
    {
      onMutate: (payload) => {
        if (payload.body.status === "archived") {
          toastId.current = toast.loading("Archiving...");
        }
      },
      onSuccess: (_, { body: { status } }) => {
        if (status === "archived")
          toast.success("Archived", { id: toastId.current });
      },
      onError: () => {
        toast.error("Something went wrong", { id: toastId.current });
      },
    },
    undefined,
  );

  const activeLead = updateLead.variables?.body ?? lead;
  const leadNumberOfUnreadEvents = activeLead.numberOfUnreadEvents ?? 0;

  const onUpdateLead = (payload: Partial<Lead>) => {
    updateLead.mutate({
      path: { organizationID: organizationId, leadID: lead.id ?? "" },
      body: { ...activeLead, ...payload },
    });
  };

  const profile = orgData.profiles.find(
    (p) => p.profileId === activeLead.profileId,
  );
  const profileName =
    profile && typeof profile.profileData.name === "string"
      ? profile.profileData.name
      : "Profile";

  const matchResult = matchData?.results?.find(
    (r) => r.profileId === activeLead.profileId,
  );
  const score =
    matchResult?.matchScore != null ? Math.round(matchResult.matchScore) : null;

  // Pass only LeadRowActiveState tabs to LeadPanelContent
  const panelState: LeadRowActiveState =
    activeTab !== "match" ? (activeTab as LeadRowActiveState) : false;

  return (
    <div
      data-lead-id={lead.id}
      className="rounded-2xl border border-gray-200 transition-colors duration-200"
    >
      {/* Card row */}
      <div className="flex items-center justify-between py-3 pr-3 pl-2 gap-4">
        {/* Left: avatar + name + subtitle */}
        <div className="flex items-center gap-2 min-w-0 pl-2">
          {profile?.profilePhotoId ? (
            <Image
              src={getImageUrl(profile.profilePhotoId)!}
              alt={profileName}
              className="w-12 h-12 rounded-xl object-cover shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 font-semibold text-lg shrink-0">
              {profileName[0] ?? "?"}
            </div>
          )}

          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm truncate">
              {profileName}
            </span>
            <span className="text-xs text-gray-500">
              Profile Version #{index + 1}
            </span>
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          {score != null && (
            <LeadButton
              onClick={() => toggleTab("match")}
              active={activeTab === "match"}
              tooltipText="Matching score — click to see details"
            >
              <div className="w-9 h-9 flex items-center justify-center gap-0.5 shrink-0">
                <span className="text-sm font-bold text-gray-900">{score}</span>
                <span className="text-sm">{scoreEmoji(score)}</span>
              </div>
            </LeadButton>
          )}
          {activeLead.profileLinkId && (
            <LeadOpenLinkButton profileLinkId={activeLead.profileLinkId} />
          )}
          {activeLead.profileLinkId && activeLead.id && (
            <LeadEditLinkButton leadId={activeLead.id} />
          )}
          {showMatrix && lead.parentLeadId && lead.id && (
            <LeadButton
              onClick={() => {
                homeState.matrixDrawerLeadId = lead.id!;
              }}
              tooltipText="View competence matrix"
            >
              <FontAwesomeIcon icon={faClipboardList} className="w-4 h-4" />
            </LeadButton>
          )}
          <LeadEventsButton
            active={activeTab === "events"}
            onClick={() => {
              toggleTab("events");
              if (leadNumberOfUnreadEvents > 0)
                onUpdateLead({ numberOfUnreadEvents: 0 });
            }}
            numberOfUnreadEvents={leadNumberOfUnreadEvents}
          />
          <LeadTemperatureButton
            priority={activeLead.priority ?? 1}
            onPriorityChange={(priority) => onUpdateLead({ priority })}
          />
          <LeadMenuButton
            className="px-3"
            profileLinkId={activeLead.profileLinkId}
            onArchive={() =>
              onUpdateLead({
                status: activeLead.status === "archived" ? "" : "archived",
              })
            }
            archived={activeLead.status === "archived"}
            onDelete={() => {
              if (lead.id) {
                deleteLead.mutate({
                  path: { organizationID: organizationId, leadID: lead.id },
                });
              }
            }}
            leadId={activeLead.id}
            profiles={orgData.profiles}
            profileId={activeLead.profileId ?? undefined}
          />
        </div>
      </div>

      {/* Match details panel */}
      {activeTab === "match" && matchResult && (
        <MatchDetailsPanel match={matchResult} />
      )}

      {/* Expandable panel — member + events only, no assignment */}
      <Suspense
        fallback={
          <div className="flex justify-center py-4">
            <LoadingSpinner size="small" />
          </div>
        }
      >
        <LeadPanelContent
          state={panelState}
          organizationId={organizationId}
          divider
          leadId={lead.id}
          onMatchingMembers={() => {}}
          onLeadCreated={() => {}}
          hideAssignment={true}
        />
      </Suspense>
    </div>
  );
};

ChildLeadRow.displayName = "ChildLeadRow";

// ---------------------------------------------------------------------------

interface MatchDetailsPanelProps {
  match: SemanticProfileMatch;
}

const scoreEmoji = (score: number) => {
  if (score >= 80) return "🎯";
  if (score >= 65) return "🔥";
  if (score >= 50) return "🌿";
  return "🌱";
};

const MatchDetailsPanel = ({ match }: MatchDetailsPanelProps) => {
  const [activeTab, setActiveTab] = useState<"domain" | "skills">("domain");
  const score = match.matchScore != null ? Math.round(match.matchScore) : null;
  const allMatchingSkills = match.matchingSkills ?? [];
  const allNonMatchingSkills = match.nonMatchingSkills ?? [];
  const skillsWeight = match.skillsWeight ?? 50;
  const domainWeight = 100 - skillsWeight;
  const domainBarWidth = (match.domainScore ?? 0) * (domainWeight / 100);
  const skillsBarWidth = (match.skillsScore ?? 0) * (skillsWeight / 100);

  return (
    <div className="border-t border-gray-100 px-4 py-4">
      <div className="flex gap-10">
        {/* Left panel */}
        <div className="flex flex-col gap-3 w-[45%] shrink-0">
          {score != null && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400 font-semibold">
                  Match score
                </p>
                <span className="flex items-center gap-1">
                  <span className="text-lg font-bold leading-none text-gray-900">
                    {score}
                  </span>
                  <span className="text-base">{scoreEmoji(score)}</span>
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-gray-900"
                  style={{ width: `${domainBarWidth}%` }}
                  title={`Domain: ${match.domainScore ?? 0}`}
                />
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${skillsBarWidth}%` }}
                  title={`Skills: ${match.skillsScore ?? 0}`}
                />
              </div>
            </div>
          )}
          {match.finalAssessment && (
            <p className="text-sm text-gray-500 italic leading-snug">
              {match.finalAssessment}
            </p>
          )}
        </div>

        {/* Right panel */}
        <div className="flex flex-col gap-3 flex-1 min-w-0 bg-gray-100 rounded-2xl p-4">
          {/* Tab toggle */}
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              onClick={() => setActiveTab("domain")}
              className={cn(
                "flex items-center gap-1 px-3 py-1 rounded-full transition-colors",
                activeTab === "domain"
                  ? "bg-gray-100 font-semibold text-gray-900"
                  : "text-gray-400",
              )}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-900" />
              Domain
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("skills")}
              className={cn(
                "flex items-center gap-1 px-3 py-1 rounded-full transition-colors",
                activeTab === "skills"
                  ? "bg-gray-100 font-semibold text-gray-900"
                  : "text-gray-400",
              )}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
              Skills
            </button>
          </div>

          {/* Tab content — fixed height to prevent row resize on tab switch */}
          <div className="h-48">
            {activeTab === "domain" && match.domainScores && (
              <div className="flex flex-col gap-2">
                {match.domainScores.map((ds, i) => (
                  <div key={i} className="flex flex-col gap-0.5 text-xs">
                    <span className="text-gray-500 truncate">{ds.domain}</span>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gray-900 rounded-full"
                        style={{
                          width: `${((ds.score_0_to_5 ?? 0) / 5) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeTab === "skills" && (
              <div className="flex flex-col gap-2">
                {match.skillsSummary && (
                  <p className="text-sm text-gray-500 italic leading-snug">
                    {match.skillsSummary}
                  </p>
                )}
                {allMatchingSkills.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1">
                    {allMatchingSkills.map((skill, i) => (
                      <EditorSkillTagButton
                        key={i}
                        text={skill.name}
                        handleClick={() => {}}
                        isStatic
                      />
                    ))}
                  </div>
                )}
                {allNonMatchingSkills.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-gray-400">Missing</span>
                    <div className="flex flex-wrap items-center gap-1">
                      {allNonMatchingSkills.map((skill, i) => (
                        <span
                          key={i}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-400 border border-red-200"
                        >
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
